import { Button, ButtonGroup, Select } from '@tunasong/ui-lib'
import type { KeyNameType } from '@tunasong/music-lib'
import { getKeyNames, prettifyNote, scaleNoteNames } from '@tunasong/music-lib'
import type { NoteName, Scale, ScaleType } from '@tunasong/schemas'
import { Typography, useDimensions } from '@tunasong/ui-lib'
import cn from 'classnames'
import type { FC } from 'react'
import styles from './scale-select.module.css'

export interface ScaleSelectProps {
  className?: string
  value?: Partial<Scale> | null
  title?: string
  fullWidth?: boolean
  variant?: KeyNameType
  allowNone?: boolean
  /**
   * `grid` lays the 12 roots out as a 6×2 grid of 44px targets, with the clear button beside it: the song key
   * popover (song key K2, #2750, spec §6). `row` is the original wrapping button row.
   */
  layout?: 'row' | 'grid'
  /** The clear button's label. @default "None" */
  noneLabel?: string
  onChange(scale: Scale | null): void
}

export const ScaleSelect: FC<ScaleSelectProps> = props => {
  const {
    value = null,
    allowNone = true,
    onChange,
    variant = 'common',
    layout = 'row',
    noneLabel = 'None',
    title,
  } = props

  const { isSmallOrSmaller } = useDimensions()

  const handleScaleType = (type: string | null) => {
    const scale = type ? { root: value?.root ?? 'C', type: type as ScaleType } : null
    onChange(scale)
  }

  const handleRoot = (note?: NoteName) => {
    onChange(note ? { root: note, type: value?.type ?? 'major' } : null)
  }

  const keyNames = getKeyNames(variant)

  const showModes = variant === 'all' || variant === 'mode'

  const noneButton = allowNone ? (
    <Button
      className={layout === 'grid' ? styles.gridNone : styles.noneButton}
      variant={value === null ? 'contained' : 'outlined'}
      color={value === null ? 'primary' : 'inherit'}
      aria-pressed={value === null}
      onClick={() => handleScaleType(null)}
    >
      {layout === 'grid' ? noneLabel : <i>{noneLabel}</i>}
    </Button>
  ) : null

  const rootButtons = scaleNoteNames.map(note => (
    <Button
      className={layout === 'grid' ? styles.gridButton : styles.noteButton}
      key={note}
      variant={value?.root === note ? 'contained' : 'outlined'}
      color={value?.root === note ? 'primary' : 'inherit'}
      aria-pressed={value?.root === note}
      onClick={() => handleRoot(note)}
    >
      {prettifyNote(note)}
    </Button>
  ))

  const scaleType = (
    <div className={styles.scaleTypeSelect} style={{ marginBottom: showModes ? 16 : 0 }}>
      <Select
        label="Scale Type"
        value={value?.type ?? ''}
        onChange={e => handleScaleType(e.target.value || null)}
        disabled={value === null}
      >
        <option value="">Select scale type...</option>
        {keyNames.map(name => (
          <option key={name} value={name}>
            {name}
          </option>
        ))}
      </Select>
    </div>
  )

  if (layout === 'grid') {
    return (
      <div className={cn(styles.gridLayout, props.className)}>
        <div role="group" aria-label={title ?? 'Root'} data-layout="grid" className={styles.rootGrid}>
          {rootButtons}
        </div>
        <div className={styles.gridActions}>
          {scaleType}
          {noneButton}
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn(styles.rootRow, props.className)}
      style={{
        alignItems: isSmallOrSmaller ? 'flex-start' : 'center',
        flexDirection: isSmallOrSmaller ? 'column' : 'row',
      }}
    >
      <Typography variant="subtitle2" className={styles.rootLabel}>
        {title ?? 'Root'}
      </Typography>
      <ButtonGroup
        variant="outlined"
        size="small"
        className={styles.buttonGroupWrap}
        aria-label={title ?? 'Root'}
        data-layout="row"
      >
        {noneButton}
        {rootButtons}
      </ButtonGroup>
      {scaleType}
    </div>
  )
}
